'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';

import { useAnalytics } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../helpers/analytics/trackEvent';
import { useGetOsName } from '../../helpers/hooks/useGetOsName';
import { AppDownloadUrlSourceType, getAppDownloadUrl } from '../../helpers/url/getAppDownloadUrl';
import { Modal } from '../Modal/Modal';
import { useIsUnsupportedInAppBrowser } from '../../helpers/hooks/useIsUnsupportedInAppBrowser';
import { useIsMobileDevice } from '../../helpers/hooks/useIsMobileDevice';
import { getAppUrlWithPath } from '../../helpers/urls';
import { ExternalLink } from '../Clickables/ExternalLink/ExternalLink';
import phone from './assets/phone.png';
import { Heading } from '../Heading/Heading';
import { AppRatingSection } from '../AppRatingSection/AppRatingSection';
import { Button } from '../Clickables/Buttons/Button';
import { useClientHasRendered } from '../../helpers/hooks';
import { usePathname, useSearchParams } from 'next/navigation';

export const AppRedirectModal = () => {
  const pathname = usePathname();
  const queryParams = useSearchParams();
  const clientHasRendered = useClientHasRendered();
  const isMobileDevice = useIsMobileDevice();

  const isAppShare = clientHasRendered && queryParams?.get('appshare') === 'true';
  const appUrlPath = queryParams?.get('appurl') ?? pathname;
  const [isModalOpen, setIsModalOpen] = useState(isMobileDevice);
  const isUserComingFromUnsupportedApp = useIsUnsupportedInAppBrowser();
  const { currentPage } = useAnalytics();

  const osName = useGetOsName();
  const appDownloadUrl = getAppDownloadUrl({
    appDownloadSourceType: AppDownloadUrlSourceType.AppRedirectModal,
    currentPageId: currentPage,
    osName,
  });

  const appUrl = appUrlPath ? getAppUrlWithPath(appUrlPath) : undefined;

  useEffect(() => {
    if (isUserComingFromUnsupportedApp && appUrl) {
      window.location.href = appUrl;
    }
  }, [appUrl, isUserComingFromUnsupportedApp]);

  if (!isAppShare) {
    return <></>;
  }

  const downloadAppClick = (): void => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'AppRedirectModal',
        type: 'download',
        page: currentPage,
      },
    });
  };

  const continueWebClick = (): void => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'AppRedirectModal',
        type: 'read-more',
        page: currentPage,
      },
    });
    setIsModalOpen(false);
  };

  return isModalOpen ? (
    <Modal
      size="full"
      outerClassName="z-modal-consent w-full"
      className="w-full"
      isOpen
      disableClose
    >
      <div className="flex h-dvh max-h-dvh flex-col items-center justify-start gap-4 sm:justify-start">
        <div className="bg-tuna-tint relative flex h-2/5 w-full items-end justify-center">
          <Image
            placeholder="blur"
            alt="Fishbrain app"
            className="w-full object-contain px-16 pt-6"
            src={phone}
            fill
            sizes="100vw"
          />
        </div>
        <div className="flex w-full flex-col items-center justify-center gap-6 px-12">
          <Heading variant="xl" className="text-center">
            Fishbrain is better in the app
          </Heading>
          <AppRatingSection className="items-center justify-center gap-6 sm:flex-row sm:justify-start" />

          <div className="flex w-full flex-col items-center justify-center gap-3 pb-6">
            <ExternalLink
              variant="buttonPrimary"
              href={appDownloadUrl}
              size="default"
              className="w-full"
              onClick={downloadAppClick}
            >
              Download
            </ExternalLink>
            <Button
              variant="buttonSecondary"
              size="default"
              className="w-full"
              onClick={continueWebClick}
            >
              Continue on web
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  ) : (
    <></>
  );
};
